import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import React from 'react'
import {CHECKOUT_STEPS, FormStep} from '../../constants/constants'
import {isStepCompleted, isStepVisible} from '../../selectors/checkout'
import {BuyerDetails} from '../buyer-details'
import {StatusScreensEntry} from '../chunks'
import {Policies} from '../policies'
import {CheckoutProps} from './interfaces'
import {Payment} from './payment'
import {PaymentWidget} from './payment/payment-widget'
import {TicketsDetails} from './tickets-details'

export class Checkout extends React.Component<CheckoutProps> {
  payment: React.RefObject<PaymentWidget> = React.createRef<PaymentWidget>()

  getSteps = () => {
    const {
      placedOrder,
      assignedTicketsEnabled,
      buyerDetails,
      validPaymentAdded,
      invoice,
      agreedWithPolicies,
      hasPolicies,
      allTicketDetailsValid,
      isTemplate,
      t,
      messages,
    } = this.props

    return CHECKOUT_STEPS.filter(step =>
      isStepVisible({step, assignedTicketsEnabled, invoice, hasPolicies, isTemplate}),
    ).map(step => {
      const stepTitle = t(`mobile.checkout.step.${step}`)
      const buyerDetailsTitle = messages?.checkout?.title ?? stepTitle
      const title = step === FormStep.BuyerDetails ? buyerDetailsTitle : stepTitle

      return {
        key: step,
        title,
        completed: isStepCompleted({
          step,
          placedOrder: placedOrder.order,
          assignedTicketsEnabled,
          buyerDetails,
          validPaymentAdded,
          agreedWithPolicies,
          hasPolicies,
          allTicketDetailsValid,
        }),
        renderStep: this.renderStep(step),
        onEdit: this.handleStepEdit,
      }
    })
  }

  handleStepEdit = async (step: FormStep) => {
    const {validPaymentAdded} = this.props

    this.props.editStep(step, validPaymentAdded)

    switch (step) {
      case FormStep.TicketsDetails:
        return this.props.setExpandedTicketIndex(0)
      case FormStep.Payment:
        return this.payment.current.expand()
      default:
        return this.props.setExpandedTicketIndex(-1)
    }
  }

  renderStep = (step: FormStep) => {
    const {t, submitCheckoutStep, currentStep} = this.props
    const props = {
      onSubmit: (data: any) => {
        if (currentStep === FormStep.Payment) {
          return this.handlePaymentStep()
        } else {
          StatusScreensEntry.preload()
          submitCheckoutStep(data)
        }
      },
      t,
    }

    switch (step) {
      case FormStep.BuyerDetails:
        return (editing: boolean) => <BuyerDetails editing={editing} {...props} />
      case FormStep.Policies:
        return (editing: boolean) => <Policies editing={editing} {...props} />
      case FormStep.TicketsDetails:
        return (editing: boolean) => <TicketsDetails editing={editing} {...props} />
      case FormStep.Payment:
        return (editing: boolean) => <Payment paymentRef={this.payment} editing={editing} {...props} />
      default:
        return () => null
    }
  }

  handlePaymentStep = async () => {
    const validPaymentAdded = await this.payment.current.validate()

    if (!validPaymentAdded) {
      this.props.setValidPaymentAdded(validPaymentAdded)
    }

    if (validPaymentAdded) {
      this.props.paymentMethodSelected(this.payment.current.props.currentPaymentMethodId)
      await this.pay()
    }

    this.props.handleNextStep()
  }

  pay = async () => {
    if (this.payment.current) {
      const status = await this.payment.current.pay()

      // @ts-expect-error
      if (status.errorCode) {
        this.props.setValidPaymentAdded(false)
        await this.handleStepEdit(FormStep.Payment)
        this.props.isMobile && focusElement({selector: hookToAttributeSelector('pw-payment-error')})
      } else {
        this.props.placeOrderButtonClicked()
      }

      return status
    }
  }

  render() {
    return this.props.children(this.getSteps(), this.props.currentStep, this.pay)
  }
}
